import { useContext, useState } from 'react';
import { AutoSizer, List, WindowScroller } from 'react-virtualized';

import config from '@/config';
import ArtworkDataContext from '@/context/ArtworkDataContext';
import useCache from '@/hooks/useCache';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import CreateNewArtlist from '@/shared-components/forms/components/CreateNewArtlist';
import InfiniteScroll from '@/shared-components/lists/components/InfiniteScroll';
import Loader from '@/shared-components/loader/Loader';
import Text from '@/shared-components/text/components/Text';

import ArtlistItem from './components/ArtlistItem';

import './index.css';

function normalizeArtlistForArtworkPicker(artlistData, artworkId) {
  if (!artlistData || typeof artlistData !== 'object') {
    return null;
  }

  const artworks = Array.isArray(artlistData.artlist_artworks)
    ? artlistData.artlist_artworks
    : [];
  const normalizedArtworkId = artworkId ? `${artworkId}` : null;

  return {
    _id: artlistData._id,
    id: artlistData.id ?? artlistData._id ?? null,
    artlist_title: artlistData.artlist_title ?? '',
    artlist_image: artlistData.artlist_image ?? null,
    artlist_artworks_total: typeof artlistData.artlist_artworks_total === 'number'
      ? artlistData.artlist_artworks_total
      : artworks.length,
    artlist_contains_artwork: typeof artlistData.artlist_contains_artwork === 'boolean'
      ? artlistData.artlist_contains_artwork
      : (normalizedArtworkId
        ? artworks.some((artwork) => `${artwork?._id ?? artwork?.id ?? artwork}` === normalizedArtworkId)
        : false),
  };
}

export default function VirtualList({ close, open }) {
  const t = useTranslations('common.shared.artwork_view.add_to_artlist');
  const { state } = useStateHandler();
  const { artworkData } = useContext(ArtworkDataContext);
  const artworkId = artworkData?._id ?? null;
  const { loading, fetchData, setFetchData, loadMoreData } = useCache(
    `${state.user_session._id}_artwork_view_artlists`,
    `${config.apis.api.url}/artlist/user/${state.user_session._id}`,
    {
      injectToken: true,
      type: "@cache/dynamic",
      invalidateWhen: ["ADD_TO_ARTLIST_FROM_ARTWORK"],
      disabled: !open,
      includeSearchQueries: {
        view: 'artwork_picker',
        artwork_id: artworkId,
      },
    }
  );
  const [openCreateArtlist, setOpenCreateArtlist] = useState(false);

  function onCreateNewArtlist(newArtlist) {
    const normalizedArtlist = normalizeArtlistForArtworkPicker(newArtlist, artworkId);
    if (!normalizedArtlist || !fetchData) {
      return;
    }
    setFetchData({
      ...fetchData,
      totalDocs: fetchData.totalDocs + 1,
      data: [normalizedArtlist, ...fetchData.data]
    });
  }

  return (
    <>
      <div className="artwork-view-add-to-artlist__content">
        {loading ? (
          <Loader className="artwork-view-add-to-artlist__spinner" thin />
        ) : fetchData.data.length === 0 ? (
          <Text className="artwork-view-add-to-artlist__no-artlist font-size-small">
            {t('empty')}
          </Text>
        ) : (
          <InfiniteScroll
            className="artwork-view-add-to-artlist__virtual-container remove-scrollbar"
            onLoadMore={loadMoreData}
            shouldLoadMore={fetchData.data.length < fetchData.totalDocs}
            offsetElements={3}
            listLength={fetchData.data.length}
          >
            {({ infiniteScrollAssigner, infiniteScrollParent }) => (
              <WindowScroller
                scrollElement={infiniteScrollParent ? infiniteScrollParent : window}
              >
                {({ height, isScrolling, onChildScroll, scrollTop }) => (
                  <AutoSizer disableHeight>
                    {({ width }) => (
                      <List
                        autoHeight
                        width={width}
                        height={height}
                        isScrolling={isScrolling}
                        onScroll={onChildScroll}
                        rowCount={fetchData.data.length}
                        rowHeight={125}
                        scrollTop={scrollTop}
                        overscanRowCount={5}
                        rowRenderer={({ index, key, style }) => (
                          <div
                            key={key}
                            style={style}
                            ref={infiniteScrollAssigner(index)}
                            className="artlist-virtual-row"
                          >
                            <ArtlistItem
                              artlistData={fetchData.data[index]}
                              close={close}
                            />
                          </div>
                        )}
                      />
                    )}
                  </AutoSizer>
                )}
              </WindowScroller>
            )}
          </InfiniteScroll>
        )}
      </div>

      <footer className="artwork-view-add-to-artlist__footer">
        <Text
          className="artwork-view-add-to-artlit__create-artlist element-non-selectable"
          onClick={() => setOpenCreateArtlist(true)}>
          {t('new_artlist')}
        </Text>
      </footer>

      <CreateNewArtlist
        open={openCreateArtlist}
        close={() => setOpenCreateArtlist(false)}
        onCreateNewArtlist={onCreateNewArtlist}
      />
    </>
  );
}
